/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../nls/StringResources',
	'react',
	'prop-types',
	'@ba/ba-ui-carbon-toolkit',
	'./internal/TopBottomNumberOfResultsSection',
	'./internal/TopBottomOperationSection',
	'./internal/TopBottomContextSection',
	'./internal/TopBottomRankSection',
	'./internal/TopBottomButtonFooterSection'
], function (
	_,
	stringResources,
	React,
	PropTypes,
	Toolkit,
	TopBottomNumberOfResultsSection,
	TopBottomOperationSection,
	TopBottomContextSection,
	TopBottomRankSection,
	TopBottomButtonFooterSection) {

	const {
		VSpacer
	} = Toolkit;


	const MIN_NUMBER = 1;
	const MAX_NUMBER = 100;
	const DEFAULT_NUMBER = 10;

	const topcount = {
		value: 'topcount',
		label: stringResources.get('topCount')
	};
	const toppercent = {
		value: 'toppercent',
		label: stringResources.get('topPercent')
	};
	const bottomcount = {
		value: 'bottomcount',
		label: stringResources.get('bottomCount')
	};
	const bottompercent = {
		value: 'bottompercent',
		label: stringResources.get('bottomPercent')
	};

	/* React components for each section of the top/bottom dialog:
	 *
	 * 1. TopBottomNumberOfResultsSection - user specifies the number or percentage of top/bottom
	 * 2. TopBottomOperationSection - user specifies the operation to perform (top/bottom number/percent)
	 * 3. TopBottomContextSection - only shown for *contextual* operations; user specifies the column to use as context
	 * 4. TopBottomRankSection - only shown if specified (right now, for tables only); user can choose to add a rank column to the vis
	 * 5. TopBottomButtonFooterSection - buttons for the user to take action
	 */
	class TopBottomComponent extends React.Component {
		static propTypes = {
			state: PropTypes.shape({
				value: PropTypes.number,
				type: PropTypes.string,
				context: PropTypes.object, /* should have member `itemId`, but other members ok too */
				rank: PropTypes.bool
			}),
			onStateChange: PropTypes.func.isRequired,
			focusReady: PropTypes.object.isRequired,
			measures: PropTypes.array, /* of columns - but that API doesn't exist in this repo */
			supportsPercentOperations: PropTypes.bool,
			supportsContext: PropTypes.bool,
			supportsRank: PropTypes.bool
		};

		static defaultProps = {
			supportsPercentOperations: false,
			supportsContext: false,
			supportsRank: false
		};

		constructor(props) {
			super(props);

			//Mirror the operation state in the React state
			this.state = _.extend(
				{
					/*
					 * is a top/bottom operation specified (i.e. not in the 'cleared' state)?
					 */
					isSet: !!props.state,

					/*
					 * is a top/bottom operation currently saved to the model (i.e. not just the transient state of this dialog)?
					 */
					isSetSaved: !!props.state,

					/*
					 * ensure `value` has a default value, even if there is no top/bottom operation in the props
					 */
					value: DEFAULT_NUMBER
				},
				props.state
			);
		}

		render() {
			const operations = this.props.supportsPercentOperations ?
				[topcount, toppercent, bottomcount, bottompercent] :
				[topcount, bottomcount];

			const sections = [];

			sections.push(
				<TopBottomNumberOfResultsSection
					focusReady={this.props.focusReady}
					min={MIN_NUMBER}
					max={MAX_NUMBER}
					value={this.state.value}
					onValueChange={value => this.changeState({ value })}
					isPercent={this.operationIsPercentage(this.state && this.state.type)}
				/>
			);

			sections.push(<VSpacer />);

			sections.push(
				<TopBottomOperationSection
					values={operations}
					value={this.state.type}
					onValueChange={type => this.changeState({ type })}
				/>
			);

			sections.push(<VSpacer />);

			if (this.props.supportsContext) {
				sections.push(
					<TopBottomContextSection
						values={this.props.measures.map(column => ({ id: column.getId(), label: column.getLabel() }))}
						value={this.getContext(this.state)}
						onValueChange={itemId => this.changeState({ context: { itemId } })}
					/>
				);
				sections.push(<VSpacer />);
			}

			if (this.props.supportsRank) {
				sections.push(
					<TopBottomRankSection
						value={this.getEffectiveRank(this.state)}
						onValueChange={rank => this.changeState({ rank })}
						enabled={this.rankIsLegal(this.state)}
					/>
				);
				sections.push(<VSpacer />);
			}

			sections.push(
				<TopBottomButtonFooterSection
					onClear={() => this.clear()}
					clearPossible={this.state.isSetSaved}
				/>
			);

			return (
				<div className="content topBottomDialog">
					{sections}
				</div>
			);
		}

		changeState(change) {
			// An operation to set the state means the user wants a top/bottom operation, so set `isSet` to true.
			const newState = {
				...this.state,
				isSet: true,
				...change
			};

			this.setState(newState);

			if (this.isValidState(newState)) {
				const {
					isSet,
					isSetSaved,
					...state
				} = newState;

				if (this.props.supportsRank) {
					state.rank = this.getEffectiveRank(newState);
				}

				this.props.onStateChange(isSet ? state : null);

				if (isSet !== isSetSaved) {
					//Update `isSetSaved` (used to compute whether Clear button should be enabled)
					this.setState({ isSetSaved: isSet });
				}
			}
		}

		getContext(state) {
			return state.context && state.context.itemId;
		}

		operationIsPercentage(operation) {
			return operation === toppercent.value || operation === bottompercent.value;
		}

		rankIsLegal(state) {
			//In cases where rank is supported, it's still only valid on count operations (i.e. not percent operations)
			return !this.operationIsPercentage(state && state.type);
		}

		getEffectiveRank(state) {
			return this.props.supportsRank && this.rankIsLegal(state) && state.rank;
		}

		clear() {
			//On clear, return the internal state to its default state when there is no top/bottom operation
			//and clear the `isSet` flag
			this.setState({
				isSet: false,
				isSetSaved: false,
				value: DEFAULT_NUMBER,
				type: null,
				context: null,
				rank: null
			});

			this.props.onStateChange(null);
		}

		/*
		 * Verifies if the current Top-N state is valid, and thus can be applied to the visualization.
		 * Essentially, if there is no state, then it is valid; or if the state is complete, it is valid.
		 * Partially defined state are invalid.
		 * @return {boolean}
		 */
		isValidState(newState) {
			//If there is a context field, if it's not set, the dialog is not in a completed state
			if (this.props.supportsContext && !this.getContext(newState)) {
				return false;
			}

			//type and value need to be set for the dialog to be in a completed state
			return !!(newState.type && newState.value);
		}
	}

	return TopBottomComponent;
});
